import { Avatar, Stack, Typography } from '@mui/material';
import {
  ISubject,
  IRecentRequests,
  ISubCategory,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { TAB_VALUE_TYPE } from '..';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ParentCategoriesListItem } from './ParentCategoriesListItem';
import { ControlledAutocomplete } from 'Components/ControlledAutoComplete';
import { useDictionary, useIsDesktop, useIsOtpLogin } from 'Hooks';
import {
  PurpleSearchIcon,
  UniversalRequestSubject1,
  UniversalRequestSubject10,
  UniversalRequestSubject11,
  UniversalRequestSubject12,
  UniversalRequestSubject13,
  UniversalRequestSubject14,
  UniversalRequestSubject15,
  UniversalRequestSubject16,
  UniversalRequestSubject2,
  UniversalRequestSubject3,
  UniversalRequestSubject4,
  UniversalRequestSubject5,
  UniversalRequestSubject6,
  UniversalRequestSubject7,
  UniversalRequestSubject8,
  UniversalRequestSubject9,
} from 'Images';
import { useEffect, useState } from 'react';
import { PreAutocompleteContent } from '../PreAutocompleteContent';

export const bySubjectIcons: { [key: number]: string } = {
  4: UniversalRequestSubject1,
  7: UniversalRequestSubject2,
  10: UniversalRequestSubject3,
  1: UniversalRequestSubject5,
  11: UniversalRequestSubject6,
  6: UniversalRequestSubject7,
  8: UniversalRequestSubject8,
  2: UniversalRequestSubject9,
  3: UniversalRequestSubject10,
  9: UniversalRequestSubject11,
  5: UniversalRequestSubject12,
  12: UniversalRequestSubject16,
  13: UniversalRequestSubject15,
};
export const byDepartmentIcons: { [key: number]: string } = {
  1: UniversalRequestSubject5,
  2: UniversalRequestSubject4,
  3: UniversalRequestSubject13,
  4: UniversalRequestSubject3,
  5: UniversalRequestSubject14,
  6: UniversalRequestSubject11,
  7: UniversalRequestSubject16,
};

interface ISubjectList {
  categoriesList: {
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  };
  tabValue: TAB_VALUE_TYPE;
  specialCategoriesData: {
    data: IRecentRequests[];
    type: string;
  };
  handleCategoryClick: (categoryId: number | null) => void;
  handleSubCategoryClick: (subCategoryId: number | null) => void;
  handleTabChange: (_: any, newValue: TAB_VALUE_TYPE) => void;
}

export const ParentCategoriesList = ({
  categoriesList,
  tabValue,
  handleCategoryClick,
  handleSubCategoryClick,
  specialCategoriesData,
  handleTabChange,
}: ISubjectList) => {
  const { isOtp } = useIsOtpLogin();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const isDesktop = useIsDesktop();
  const [showSuggested, setShowSuggested] = useState<boolean>(true);

  const [inputValue, setInputValue] = useState('');

  const getSubSubjectIdFromOptionValue = (option: string) =>
    categoriesList.subSubjects.find(
      (subSubject) =>
        `${subSubject.parentTitle} - ${subSubject.title}` === option
    )!.id;

  const getDepartmentIdFromOptionValue = (option: string) =>
    categoriesList.departments.find(
      (department) => `${department.title}` === option
    )!.id;

  const goToSelectedDepartment = (categoryId: number) => {
    handleTabChange(null, TAB_VALUE_TYPE.DEPARTMENT);
    handleCategoryClick(categoryId);
  };

  const onAutocompleteChange = (value: string) => {
    const isSubSubject = value.includes('-');
    const selectedOptionId = isSubSubject
      ? getSubSubjectIdFromOptionValue(value)
      : getDepartmentIdFromOptionValue(value);
    if (!isOtp) {
      const selectedOption = isSubSubject
        ? categoriesList.subSubjects.find(
            (subSubject) => subSubject.id === selectedOptionId
          )
        : categoriesList.departments.find(
            (department) => department.id === selectedOptionId
          );
      if (isSubSubject && (selectedOption as ISubCategory)?.link)
        window.location.assign((selectedOption as ISubCategory)?.link || '');
      else if (selectedOption) goToSelectedDepartment(selectedOption.id);
    }
    isSubSubject
      ? handleSubCategoryClick(selectedOptionId)
      : goToSelectedDepartment(selectedOptionId);
  };

  const getIconById = (id: number) =>
    (tabValue === TAB_VALUE_TYPE.SUBJECTS
      ? bySubjectIcons[id]
      : byDepartmentIcons[id]) || bySubjectIcons[1];

  useEffect(() => {
    if (inputValue) {
      setShowSuggested(false);
    } else {
      setShowSuggested(true);
    }
  }, [inputValue]);

  const getOptions = (): string[] => {
    const result = categoriesList.subSubjects.map((subSubject) => {
      return `${subSubject.parentTitle} - ${subSubject.title}`;
    });

    const departments = categoriesList.departments.map((department) => {
      return `${department.title}`;
    });

    return [...new Set(result), ...departments];
  };

  return (
    <Stack
      width={isDesktop ? '100%' : '91.875vw'}
      maxWidth={isDesktop ? '854px' : undefined}
      m='0 auto'
      pt='42px'
    >
      <span className='sr-only' id='search-description'>
        {dictionary.searchDescription}
      </span>
      <ControlledAutocomplete
        inputProps={{ 'aria-describedby': 'search-description' }}
        setValue={onAutocompleteChange}
        setInputValue={(value) => setInputValue(value)}
        label={dictionary.documentSearch}
        options={getOptions()}
        inputLabelProps={{
          shrink: false,
          sx: {
            display: inputValue.length !== 0 ? 'none' : 'block',
            '&.Mui-focused': {
              display: 'none',
            },
          },
        }}
        style={{
          width: isDesktop ? '500px' : '100%',
          m: isDesktop ? undefined : '0 auto',
          borderRadius: '34px',
          bgcolor: '#fff',

          '&&&:has(input[dropdown="open"])': {
            borderRadius: '16px 16px 0 0',
          },
          '&&& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '34px',
            borderColor: 'primary.shade_200',
            borderWidth: '1px',
          },
          '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
            borderColor: 'primary.main',
            borderRadius: '16px 16px 0 0',
            borderBottom: '0',
            borderWidth: '1px',
          },
          '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
            border: '1px solid',
            borderColor: 'primary.shade_200',
          },

          '&&& .MuiInputLabel-root': {
            paddingX: '8px',
            paddingY: isDesktop ? '4px' : '0px',
          },
          '&&& .MuiInputLabel-shrink': {
            padding: '0',
          },
          '&&& .MuiOutlinedInput-root': {
            height: isDesktop ? '56px' : '44px',
            borderRadius: '0px',
          },
          '&&& .MuiOutlinedInput-input': {
            padding: '0 15px',
          },
        }}
        endAdornment={
          <Avatar
            src={PurpleSearchIcon}
            alt={''}
            sx={{
              mt: isDesktop ? undefined : '-1px',
              mr: isDesktop ? '4px' : undefined,
              height: isDesktop ? '40px' : '32px',
              width: isDesktop ? '40px' : '32px',
            }}
          />
        }
        noResultsText={generalDictionary.noOptions}
        renderOptionsProps={{
          fontSize: isDesktop ? '20px' : '18px',
          fontWeight: isDesktop ? undefined : '600',
          lineHeight: '24px',
          color: 'text.secondary',
        }}
        paperSx={{
          borderRadius: '0 0 16px 16px',
          outline: '0px',
          border: '1px solid',
          borderColor: 'primary.main',
          borderTop: '0px',
          maxHeight: undefined,
          '& ul': {
            maxHeight: '250px',
            bgcolor: '#fff',
            position: 'relative',
          },
        }}
        paperComponent={
          showSuggested && (
            <PreAutocompleteContent
              data={specialCategoriesData.data}
              type={specialCategoriesData.type}
              handleSubCategoryClick={handleSubCategoryClick}
            />
          )
        }
      />
      <fieldset aria-describedby='select-parent-description'>
        <legend>
          {tabValue === TAB_VALUE_TYPE.SUBJECTS
            ? dictionary.whatsTheSubject
            : dictionary.whichDepartment}
        </legend>
        <span className='sr-only' id='select-parent-description'>
          {dictionary.selectParentDescription}
        </span>
        <Typography
          alignSelf='center'
          width={'100%'}
          variant={'body1'}
          m='42px 0 16px'
        >
          {tabValue === TAB_VALUE_TYPE.SUBJECTS
            ? dictionary.whatsTheSubject
            : dictionary.whichDepartment}
        </Typography>
        <Stack
          direction='row'
          width={'100%'}
          alignItems='center'
          justifyContent={isDesktop ? 'flex-start' : 'center'}
          flexWrap='wrap'
          m='0 auto'
          gap='16px'
          columnGap={isDesktop ? undefined : '16px'}
        >
          {categoriesList[
            tabValue === TAB_VALUE_TYPE.SUBJECTS ? 'subjects' : 'departments'
          ].map((category) => (
            <ParentCategoriesListItem
              key={category.id}
              category={category}
              onClick={() => handleCategoryClick(category.id)}
              svgIcon={getIconById(category.id)}
              tabValue={tabValue}
            />
          ))}
        </Stack>
      </fieldset>
    </Stack>
  );
};
